import { ValidationErrors } from '@angular/forms';
import { MatFormFieldAppearance } from '@angular/material/form-field';
import { Observable, of, Subject } from 'rxjs';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ComponentType } from '@angular/cdk/portal';
import { Injector } from '@angular/core';
import { FormlyTemplateOptions, FormlyFieldConfig } from '@ngx-formly/core';
import { Store } from '@ngxs/store';
import { RestService } from '../../public-api';
import { ScGenericFormComponent } from './../components/sc-generic-form/sc-generic-form.component';
import { ScGoogleMapSearchConfig, ScSearchConfig } from './generic-form.model';

type ScFormFieldTypes =
  | 'datepicker'
  | 'textarea'
  | 'input'
  | 'select'
  | 'multiselect'
  | 'image-upload'
  | 'checkbox'
  | 'info'
  | 'number'
  | 'slide-toggle'
  | 'radio'
  | 'autocomplete'
  | 'toggle'
  | 'search'
  | 'location';

export class ScBaseFormField {
  type: ScFormFieldTypes;
  field: string;
  name: string;
  required?: boolean;
  validators?: ValidationErrors[];
  default?: any;
  appearance?: MatFormFieldAppearance;
  hint?: string;
  prefix?: string;
  suffix?: string;
  step?: number;
}

export class ScInfoFormField {
  type: 'info';
  content: string;
}

export class ScCheckboxFormField extends ScBaseFormField {
  type: 'checkbox';
  options: {
    truthy: string;
    falsy: string;
  };
}

export class ScRadioField extends ScBaseFormField {
  type: 'radio';
  options: any[];
}

export class ScSlideToggleField extends ScBaseFormField {
  type: 'slide-toggle';
  options: {
    truthy: string;
    falsy: string;
    position?: 'left' | 'right';
  };
}

export class ScAutocompleteField extends ScBaseFormField {
  type: 'autocomplete';
  options: {
    fields: Observable<{ id: string; name: any }[]>;
  };
}

export class ScSearchFormField extends ScBaseFormField {
  type: 'search';
  options: {
    config: ScSearchConfig;
    uppercase: boolean;
  };
}

export class ScLocationFormField extends ScBaseFormField {
  type: 'location';
  options: {
    config: ScGoogleMapSearchConfig;
  };
}

export class ScBaseSelectField extends ScBaseFormField {
  type: 'select';
  options: ScSelectOptions;
}

class ScSelectFormField extends ScBaseSelectField {}

export type ScFormField =
  | ScBaseFormField
  | ScInfoFormField
  | ScSelectFormField
  | ScCheckboxFormField
  | ScRadioField
  | ScSlideToggleField
  | ScAutocompleteField
  | ScLocationFormField
  | ScSearchFormField;

export interface ScGridContent {
  desc: string;
  linkSrc: string;
  blobSrc: string;
}

class ScOptionField {
  id: string | number;
  name: string | number;
}

class ScBaseSelectOption {
  type: 'internal' | 'external';
}

export class ScInternalOption extends ScBaseSelectOption {
  type: 'internal';
  fields: ScOptionField[];
}

export class ScSelectFetchOptions {
  prop: ScColPropFunction | string;
  itemId: string;
  endpoint: string;
  filters?: any[];
}

export class ExternalOption extends ScBaseSelectOption {
  type: 'external';
  fetchOptions: ScSelectFetchOptions;
}

export type ScSelectOptions = ScInternalOption | ExternalOption;

export class ScSelectField {
  type: 'select';
  options: ScSelectOptions;
}

export enum ScTableAction {
  Create,
  Edit,
  Delete,
  Link,
  View,
  Duplicate,
  TextInfo,
  RowActionBtn,
  Select,
  ColSelect,
  Upload,
  Navigate,
  Confirm,
  Cancel
}

class ScTableButtonAction {
  actionType: ScTableAction;
  name: string;
  key?: string;
  icon: string;
}

type ScColumnType =
  | 'text'
  | 'date'
  | 'checkbox'
  | 'link'
  | 'routelink'
  | 'externallink'
  | 'action'
  | 'button'
  | 'task'
  | 'icon'
  | 'datepicker';

export interface ScTableSummary {
  label: string;
  result: any;
  type?: 'currency' | string;
}

export class ScBaseTableColumn {
  name: string;
  type?: ScColumnType;
  class?: string[] | ScColClassFunction;
  summary?: (data: any[]) => ScTableSummary[];
  searchable?: boolean;
  currency?: string;
  translate?: string;
  sort?: boolean;
  sortCompare?: ScColSortCompare;
  isDefaultSort?: boolean;
  startSortDirection?: 'asc' | 'desc';
  sortKey?: string;
  hide?: ScColPropFunction;
}

export type ScColSortCompare = (a: number | string, b: number | string, isAsc: boolean) => boolean;

export type ScColPropFunction = (row: any) => any;

export type ScColClassFunction = (row: any) => string[];

export class ScDefaultTableColumn extends ScBaseTableColumn {
  prop: string | ScColPropFunction;
}

export class ScIconTableColumn extends ScDefaultTableColumn {
  type: 'icon';
  hint?: string;
}

export class ScRouteLinkColumn extends ScBaseTableColumn {
  type: 'routelink';
  prop?: string | ScColPropFunction;
  options?: any;
}

export class ScActionColumn extends ScDefaultTableColumn {
  type: 'action';
  items: ScTableButtonAction[];
}

export class ScButtonColumn extends ScDefaultTableColumn {
  type: 'button';
  value: string;
  color: 'primary' | 'accent' | 'warn';
}

export type ScTableColumn = ScDefaultTableColumn | ScRouteLinkColumn | ScButtonColumn | ScActionColumn;

export const DEFAULT_TABLE_ACTIONS: ScActionColumn = {
  name: 'Action',
  prop: 'action',
  type: 'action',
  items: [
    {
      name: 'Edit',
      key: 'edit',
      icon: 'edit',
      actionType: ScTableAction.Edit
    },
    {
      name: 'Delete',
      key: 'delete',
      icon: 'delete',
      actionType: ScTableAction.Delete
    }
  ]
};

export class ScEndpointFilter {
  prop: string;
  comparison: 'equals' | 'not' | 'contains' | 'notContains';
  value: any;
  constructor(filter: ScEndpointFilter) {
    this.prop = filter.prop;
    this.comparison = filter.comparison;
    this.value = filter.value;
  }
}

export type ScFormFieldsFunction = () => ScFormField[] | ScFieldConfig[] | Observable<ScFormField[] | ScFieldConfig[]>;

export interface ScEndpointReqScope {
  routeSnapshot: ActivatedRouteSnapshot;
  injector: Injector;
  rest: RestService;
  store: Store;
  _unsubscribe$?: Subject<any>;
}

export class ScEndpointConfig {
  endpoint: string;
  filters?: ScEndpointFilter[];
  beforeRequest?: (data: any) => any;
  afterRequest?: (data: any) => any;
  beforeLoad?: () => Observable<ScEndpointFilter[]> | ScEndpointFilter[];
  filter?: () => Observable<ScEndpointFilter[]>;

  constructor(config: Partial<ScEndpointConfig>) {
    this.endpoint = config.endpoint;
    this.filters = config.filters;
    this.beforeRequest = config.beforeRequest;
    this.afterRequest = config.afterRequest;
    this.beforeLoad = config.beforeLoad;

    if (!this.filters) {
      this.filters = [];
    }

    if (!this.beforeRequest) {
      this.beforeRequest = v => v;
    }

    if (!this.afterRequest) {
      this.afterRequest = v => v;
    }

    if (!this.beforeLoad) {
      this.beforeLoad = () => of(this.filters);
    }

    if (!this.filter) {
      this.filter = () => of(this.filters);
    }
  }

  public destroy() {}
}

export type ScTemplateOtp = (rest: RestService) => Observable<any[]>;

export interface ScTemplateOptions extends FormlyTemplateOptions {
  options?: any[] | Observable<any[]> | ScTemplateOtp | any;
}

export interface ScFieldConfig extends FormlyFieldConfig {
  templateOptions?: ScTemplateOptions;
}

export class ScCrudConfiguration {
  load: Partial<ScEndpointConfig>;
  get?: Partial<ScEndpointConfig>;
  create?: Partial<ScEndpointConfig>;
  update?: Partial<ScEndpointConfig>;
  remove?: Partial<ScEndpointConfig>;
  formFields?: ScFormField[] | ScFieldConfig[] | ScFormFieldsFunction;
  formComponent?: ComponentType<any>;
  itemId?: string;
  isForTesting?: boolean;
  isForFormly?: boolean;

  constructor(config: Partial<ScCrudConfiguration>) {
    this.load = config.load;

    if (config.get) {
      this.get = config.get;
    }

    if (config.create) {
      this.create = config.create;
    }

    if (config.update) {
      this.update = config.update;
    }

    if (config.remove) {
      this.remove = config.remove;
    }

    this.formFields = config.formFields;
    this.formComponent = config.formComponent;
    this.itemId = config.itemId;
    this.isForFormly = config.isForFormly;

    if (!this.load) {
      throw new Error('Load configuration is missing');
    }

    if (!this.formFields) {
      this.formFields = [];
    }

    if (!this.formComponent) {
      this.formComponent = ScGenericFormComponent;
    }

    if (!this.itemId) {
      this.itemId = 'id';
    }

    if (!this.isForTesting) {
      this.isForTesting = false;
    }

    this.load = new ScEndpointConfig(this.load);
    if (this.get) {
      this.get = new ScEndpointConfig(this.get);
    }

    if (this.create) {
      this.create = new ScEndpointConfig(this.create);
    }

    if (this.update) {
      this.update = new ScEndpointConfig(this.update);
    }

    if (this.remove) {
      this.remove = new ScEndpointConfig(this.remove);
    }
  }
}

export class ScCrudRules {
  canEdit = true;
  canDelete = true;
  canCreate = true;
  constructor(rules?: ScCrudRules) {
    if (rules) {
      this.canEdit = rules.canEdit;
      this.canDelete = rules.canDelete;
      this.canCreate = rules.canCreate;
    }
  }
}

export enum ScSelectionType {
  single = 'single',
  multi = 'multi',
  multiClick = 'multiClick',
  cell = 'cell',
  checkbox = 'checkbox'
}

export const DEFAULT_VIEW_TOGGLE_ACTIONS = [
  {
    name: 'Grid',
    type: 'grid',
    icon: 'view_module',
    tooltip: 'Grid View'
  },
  {
    name: 'Table',
    type: 'table',
    icon: 'view_column',
    tooltip: 'Table View'
  }
];

class ScTableViewToggleAction {
  name: string;
  icon: string;
  tooltip: string;
  type: string;
}

export class ScGridActions extends ScActionColumn {}

export type ScGridItems = [ScGridContent, ScGridActions];

export class ScBaseTableViewConfig {
  title: string;
  compact: boolean;
  key: string;
  fullHeight: boolean;
  crud: ScCrudConfiguration;
  filters: ScTableViewFilter[];
  gridItems: ScGridItems | [];
  columns: ScTableColumn[];
  toggleActions: ScTableViewToggleAction[];
  summary: boolean;

  constructor(public config: Partial<ScBaseTableViewConfig>) {
    if (!config) {
      throw new Error('View configuration is missing!');
    }
    this.columns = config.columns;
    this.title = config.title;
    this.compact = config.compact;
    this.crud = config.crud;
    this.filters = config.filters;
    this.gridItems = config.gridItems;
    this.toggleActions = config.toggleActions;
    this.summary = config.summary;
    this.fullHeight = config.fullHeight;
    this.key = config.key;

    this.columns = this._setColumns(this.columns);

    if (this.compact && this.crud) {
      console.warn('Table create button will be hidden since you have chosen to make compact and also make it a crud!');
      // eslint-disable-next-line no-console
      console.info('Consider setting compact to false.');
    }

    if (!this.compact) {
      this.compact = false;
    }

    if (!this.filters) {
      this.filters = [];
    }

    if (!this.title && !this.compact) {
      console.warn('Please provide a title for this table');
    }

    if (this.crud) {
      this.crud = new ScCrudConfiguration(this.crud);
    }

    if (!this.gridItems) {
      this.gridItems = [];
      this.toggleActions = [];
    }

    if (this.gridItems && !this.toggleActions) {
      this.toggleActions = DEFAULT_VIEW_TOGGLE_ACTIONS;
    }
  }

  public setLoadFilters(filters: ScEndpointFilter[]): ScBaseTableViewConfig {
    if (!this.crud) {
      console.warn('Crud config is missing from view configuration!');
      return;
    }

    if (!filters) {
      return;
    }

    this.crud.load.filters = filters;

    return this;
  }

  public destroy() {
    if (this.crud) {
      this.crud.load.destroy();
    }
  }

  private _setColumns(columns: ScTableColumn[]): ScTableColumn[] {
    if (!this.columns) {
      throw new Error('You must provide at least one table column');
    }
    const actionColumn = columns.find((c: ScTableColumn) => c.type === 'action');

    if (!actionColumn && this.crud && this.crud.create && this.crud.update && !this.compact) {
      return [...columns, DEFAULT_TABLE_ACTIONS];
    }

    return columns;
  }
}

export type ScTableViewConfig = Partial<ScBaseTableViewConfig>;

type ScFromFilterTypes = 'toggle' | 'input' | 'datepicker' | 'select';

export class ScBaseViewFilter {
  type: ScFromFilterTypes;
  field: string;
  name: string;
}

export interface ScTableViewFilter {
  field: any;
  filter: any;
}

export interface ScTableActionSelection {
  type: ScTableAction;
  data: any;
  colProp?: string;
  colValue?: any;
  col?: any;
}

export enum ScRowAction {
  Add,
  Update,
  Delete
}

export interface ScRowUpdate {
  row: any;
  type: ScRowAction;
}
